/** Thin fetch wrapper. Adds TG initData to every request as Authorization. */
import { tgInitData } from "./tg";

const BASE = ""; // proxied via vite to http://localhost:8009 in dev

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const initData = tgInitData();
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (initData) headers.set("X-Telegram-Init-Data", initData);

  const res = await fetch(BASE + path, { ...init, headers });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} on ${path}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  health: () => call<{ status: string; redis: string }>("/healthz"),

  me: () => call<Me>("/api/me"),
  digest: () => call<Digest>("/api/digest/today"),
  alertsTop: () => call<{ items: AlertItem[]; total: number }>("/api/alerts/top"),
  alertsAll: () => call<{ items: AlertItem[]; total: number }>("/api/alerts/all"),

  dealCard: (leadAmoId: number) => call<DealCard>(`/api/deals/${leadAmoId}`),
  dealsMeta: () => call<DealsMeta>("/api/deals-meta"),
  updateDeal: (leadAmoId: number, patch: DealPatch) =>
    call<{ ok: boolean; changed: string[]; closed_tasks?: number }>(`/api/deals/${leadAmoId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),
  addDealNote: (leadAmoId: number, text: string) =>
    call<{ ok: boolean }>(`/api/deals/${leadAmoId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    }),
  managerCard: (amoUserId: number) => call<ManagerCard>(`/api/managers/${amoUserId}`),
  managerMessage: (amoUserId: number, text: string) =>
    call<{ ok: boolean }>(`/api/managers/${amoUserId}/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    }),

  analyticsFunnel: () => call<FunnelData>("/api/analytics/funnel"),
  analyticsManagers: () => call<{ managers: ManagerStats[] }>("/api/analytics/managers"),
  analyticsManagerFunnel: (periodDays = 30) =>
    call<{ period_days: number; managers: ManagerFunnel[] }>(
      `/api/analytics/manager_funnel?period_days=${periodDays}`,
    ),

  dealsList: (stage?: string) =>
    call<{ items: LeadListItem[]; total: number; filter: string }>(
      stage ? `/api/leads/list?stage=${stage}` : "/api/leads/list",
    ),
  tasksList: (filter?: string) =>
    call<{ items: TaskListItem[]; total: number; filter: string }>(
      filter ? `/api/tasks/list?filter=${filter}` : "/api/tasks/list",
    ),

  myDeals: (sort: "new" | "silent" | "value" = "new") =>
    call<{ items: MyDealItem[]; total: number; sort: string }>(`/api/leads/mine?sort=${sort}`),
  myTasks: (range: "today" | "today_tomorrow" | "week" | "overdue" | "all" = "today_tomorrow") =>
    call<{ items: MyTaskItem[]; total: number; range: string }>(`/api/tasks/mine?range=${range}`),

  search: (q: string) =>
    call<SearchResult>(`/api/search?q=${encodeURIComponent(q)}`),

  completeTask: (taskAmoId: number) =>
    call<{ ok: boolean }>(`/api/tasks/${taskAmoId}/complete`, { method: "POST" }),
  deleteTask: (taskAmoId: number) =>
    call<{ ok: boolean }>(`/api/tasks/${taskAmoId}`, { method: "DELETE" }),

  createDeal: (input: DealCreateInput) =>
    call<{ ok: boolean; lead_amo_id: number; name: string; task_amo_id: number | null; contact_reused: boolean }>(
      "/api/deals",
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) },
    ),
  updateDealContact: (leadAmoId: number, contactAmoId: number, v: { name?: string; phone?: string }) =>
    call<{ ok: boolean; changed: string[] }>(`/api/deals/${leadAmoId}/contacts/${contactAmoId}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(v),
    }),
  addDealContact: (leadAmoId: number, v: { name?: string; phone?: string }) =>
    call<{ ok: boolean; contact_amo_id: number; reused: boolean }>(`/api/deals/${leadAmoId}/contacts`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(v),
    }),
  callDetail: (id: number) => call<CallDetail>(`/api/calls/${id}`),
  callsList: (filter: string, days: number) =>
    call<{ items: CallListItem[]; total: number }>(`/api/calls?filter=${filter}&days=${days}`),
  callsStats: (days: number) => call<CallsStats>(`/api/analytics/calls?days=${days}`),
  deleteDeal: (leadAmoId: number) =>
    call<{ ok: boolean; tasks: number }>(`/api/deals/${leadAmoId}`, { method: "DELETE" }),
  archiveDeal: (leadAmoId: number) =>
    call<{ ok: boolean }>(`/api/leads/${leadAmoId}/archive`, { method: "POST" }),
  unarchiveDeal: (leadAmoId: number) =>
    call<{ ok: boolean }>(`/api/leads/${leadAmoId}/unarchive`, { method: "POST" }),
  archivedDeals: () =>
    call<{ items: MyDealItem[]; total: number }>(`/api/leads/archived`),

  createTaskOnDeal: (leadAmoId: number, text: string, responsibleAmoId: number, completeTillIso: string) =>
    call<{ ok: boolean; task_amo_id: number }>(`/api/deals/${leadAmoId}/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        responsible_amo_id: responsibleAmoId,
        complete_till_iso: completeTillIso,
      }),
    }),

  pingTask: (taskAmoId: number) =>
    call<{ ok: boolean; sent: Array<{ tg_id: number; message_id: number }>; count?: number }>(
      `/api/tasks/${taskAmoId}/ping`,
      { method: "POST" },
    ),

  /** «🔔 Пнуть менеджера» — note в lead/contact + срочная задача +1ч (+ SMS если настроен). Director only. */
  pokeTaskOwner: (taskDbId: number) =>
    call<{
      ok: boolean;
      note_added: boolean;
      urgent_task_amo_id: number | null;
      sms: { ok: boolean; provider: string; detail: string } | null;
      manager_name: string;
    }>(`/api/tasks/${taskDbId}/poke`, { method: "POST" }),
  pingDeal: (leadAmoId: number, note?: string) =>
    call<{ ok: boolean; sent: Array<{ tg_id: number; message_id: number }> }>(
      `/api/deals/${leadAmoId}/ping`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: note ?? "" }),
      },
    ),

  /** «🔔 Пнуть» на уровне сделки. Кладёт ноту в lead + создаёт срочную задачу +1ч
   *  на ответственного менеджера (+ SMS если SMS_PROVIDER настроен).
   *  amoCRM сама шлёт менеджеру email/push на новые ноту и задачу. */
  pokeDeal: (leadAmoId: number) =>
    call<{
      ok: boolean;
      note_added: boolean;
      urgent_task_amo_id: number | null;
      sms: { ok: boolean; provider: string; detail: string } | null;
      manager_name: string;
    }>(`/api/deals/${leadAmoId}/poke`, { method: "POST" }),

  /** All active team members (manager + director, no hidden) — for assignee pickers. */
  team: () =>
    call<{ items: Array<{ amo_id: number; name: string; role: string; is_me: boolean }> }>(
      "/api/team",
    ),

  // ---- installer (монтажник) — deals at Доставка/Реализация + manual assign ----
  installerObjects: () =>
    call<{ items: InstallerObject[]; total: number }>("/api/installer/objects"),
  installerStep: (leadAmoId: number, step: "drive" | "measure" | "deliver" | "install") =>
    call<{ ok: boolean; label: string; step: string }>(`/api/installer/objects/${leadAmoId}/step`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }),
  installerPhoto: (leadAmoId: number, file: File) => {
    const fd = new FormData();
    fd.append("photo", file, file.name || "object.jpg");
    return call<{ ok: boolean; attached: boolean; lead_name: string }>(
      `/api/installer/objects/${leadAmoId}/photo`,
      { method: "POST", body: fd },
    );
  },
  assignInstaller: (leadAmoId: number) =>
    call<{ ok: boolean }>(`/api/deals/${leadAmoId}/assign-installer`, { method: "POST" }),
  unassignInstaller: (leadAmoId: number) =>
    call<{ ok: boolean }>(`/api/deals/${leadAmoId}/assign-installer`, { method: "DELETE" }),

  voiceUpload: (audio: Blob, ctx?: { dealId?: number }) => {
    const fd = new FormData();
    // Filename extension matters for Whisper: .webm for WebM/Opus (Chrome/Android),
    // .m4a for audio/mp4 (iOS Safari). Match blob.type to give Whisper the right hint.
    const isMp4 = (audio.type || "").includes("mp4") || (audio.type || "").includes("aac");
    const filename = isMp4 ? "voice.m4a" : "voice.webm";
    fd.append("audio", audio, filename);
    if (ctx?.dealId) fd.append("deal_id", String(ctx.dealId));
    return call<VoiceParsed>("/api/voice/upload", { method: "POST", body: fd });
  },
  parseText: (text: string, ctx?: { dealId?: number }) =>
    call<VoiceParsed>("/api/voice/parse-text", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, deal_id: ctx?.dealId ?? null }),
    }),
  voiceApply: (commandId: number) =>
    call<{ ok: boolean; message: string }>(`/api/voice/apply/${commandId}`, { method: "POST" }),
};

// --- types (mirror dataclasses in kvin/core/digest.py) ---
export interface Me {
  tenant_code: string;
  tg_id: number;
  tg_first_name: string | null;
  tg_last_name: string | null;
  role: "director" | "manager" | "installer" | "integration" | "unknown";
  amo_user_amo_id: number | null;
  amo_user_name: string | null;
  amo_user_email: string | null;
  is_admin: boolean;
}

export interface Kpi {
  label: string;
  value: string;
  unit: string;
  delta_text: string;
  delta_positive: boolean;
  tone: "default" | "accent" | "warn" | "danger";
}

export interface ManagerSummary {
  amo_id: number;
  name: string;
  deals_count: number;
  open_tasks: number;
  overdue: number;
  silent_long: number;
  tone: "ok" | "warn" | "danger";
  status_text: string;
}

export interface AlertItem {
  lead_amo_id: number;
  deal_name: string;
  vip: boolean;
  price_rub: number;
  manager_name: string;
  stage_name: string;
  age_days: number;
  reason: string;
  tone: "danger" | "warn" | "vip";
}

export interface Digest {
  today_ru: string;
  hero_count: number;
  hero_bullets: string[];
  kpi: Kpi[];
  managers: ManagerSummary[];
  alerts: AlertItem[];
  alerts_total: number;
}

export interface Contact {
  amo_id: number;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface InstallerObject {
  lead_amo_id: number;
  lead_name: string;
  stage_amo_id: number;
  stage_name: string | null;
  contacts: Array<{ name: string; phone: string | null }>;
  tech: Record<string, string>;
  steps_done: { drive: boolean; measure: boolean; deliver: boolean; install: boolean };
  manual: boolean;
  amo_url: string;
}

export interface OpenTask {
  amo_id: number;
  db_id?: number;
  text: string;
  complete_till: string | null;
  responsible_user_amo_id: number | null;
  responsible_name: string | null;
  tg_pingable: boolean;
}

export interface DealCard {
  amo_id: number;
  name: string;
  price: number | null;
  stage_name: string | null;
  pipeline_amo_id: number;
  status_amo_id: number;
  responsible_name: string | null;
  responsible_user_amo_id?: number | null;
  installer_manual?: boolean;
  installer_stage?: boolean;
  created_at: string | null;
  updated_at: string | null;
  custom_fields: Record<string, string[]>;
  contacts: Contact[];
  open_tasks: OpenTask[];
  calls?: CallRow[];
  amo_url: string | null;
  can_edit?: boolean;
  can_reassign?: boolean;
}

export interface DealStage { amo_id: number; name: string; color: string | null; }
export interface DealPipeline { amo_id: number; name: string; is_main: boolean; statuses: DealStage[]; }
export interface DealField {
  id: string;
  label: string;
  type: string;
  enums: { id: number; value: string }[];
}
export interface CallRow {
  id: number;
  direction: "in" | "out";
  answered: boolean;
  duration_sec: number;
  called_at: string;
  manager_name: string | null;
  phone: string | null;
  lead_amo_id: number | null;
  summary: string | null;
  score: number | null;
  is_bad: boolean;
  call_type: string | null;
  next_step: string | null;
  verdict: string | null;
  has_transcript: boolean;
  record_url: string | null;
  missed?: boolean;
  callback_min?: number | null;
}
export interface CallListItem extends CallRow { lead_name: string | null; }
export interface CallsStats {
  days: number; incoming: number; outgoing: number; answered_in: number; answered_pct: number | null;
  missed: number; not_called_back: number; callback_median_min: number | null; analyzed: number; bad: number;
  target: number; non_target: number;
  by_staff: { name: string; calls: number; bad: number; avg_score: number | null }[];
  objections: { text: string; count: number }[];
}
export interface CallDetail extends CallRow { transcript: string | null; analysis: Record<string, unknown>; }
export interface DealCreateInput {
  name: string;
  pipeline_amo_id?: number;
  status_amo_id?: number;
  price?: number;
  responsible_user_amo_id?: number;
  contact_name?: string;
  phone?: string;
  task_text?: string;
  task_complete_till_iso?: string;
}
export interface DealsMeta { pipelines: DealPipeline[]; fields: DealField[]; }
export type DealFieldValue = string | number | number[] | null;
export interface DealPatch {
  name?: string;
  price?: number;
  pipeline_amo_id?: number;
  status_amo_id?: number;
  responsible_user_amo_id?: number;
  custom_fields?: Record<string, DealFieldValue>;
}

export interface ManagerDeal {
  amo_id: number;
  name: string;
  price: number;
  stage_name: string;
  silence_days: number;
  updated_at: string | null;
}

export interface ManagerTask {
  amo_id: number;
  text: string;
  complete_till: string | null;
  overdue_days: number;
  entity_amo_id: number | null;
  entity_type: string | null;
  lead_name: string | null;
}

export interface ManagerCard {
  amo_id: number;
  name: string;
  email: string | null;
  is_admin: boolean;
  deals: ManagerDeal[];
  open_tasks: ManagerTask[];
  totals: {
    deals: number;
    deal_value_rub: number;
    open_tasks: number;
    overdue: number;
  };
}

export interface FunnelStage {
  amo_id: number;
  name: string;
  sort: number;
  color: string | null;
  count: number;
  value_rub: number;
  is_closed: boolean;
  is_won: boolean;
}

export interface FunnelPipeline {
  amo_id: number;
  name: string;
  is_main: boolean;
  stages: FunnelStage[];
}

export interface FunnelData {
  pipelines: FunnelPipeline[];
  won_30d: number;
  lost_30d: number;
  conversion_30d: number;
}

export interface ManagerFunnel {
  amo_id: number;
  name: string;
  is_admin: boolean;
  funnel: {
    new_in_period: number;
    processed: number;
    kp_reached: number;
    contract_reached: number;
    won: number;
    lost: number;
  };
  conversion: {
    processed_to_kp: number;
    kp_to_contract: number;
    processed_to_won: number;
  };
}

export interface LeadListItem {
  amo_id: number;
  name: string;
  price_rub: number;
  stage_name: string;
  manager_name: string;
  silence_days: number;
  updated_at: string | null;
}

export interface TaskListItem {
  amo_id: number;
  db_id: number;
  text: string;
  complete_till: string | null;
  overdue_days: number;
  responsible_name: string;
  responsible_amo_id: number | null;
  lead_amo_id: number | null;
  lead_name: string | null;
  entity_type: string | null;     // 'leads' | 'contacts' | 'companies' | null
  entity_amo_id: number | null;
  amo_url: string | null;         // direct deep-link to amoCRM (lead/contact/company detail)
}

export interface ManagerStats {
  amo_id: number;
  name: string;
  is_admin: boolean;
  active_deals: number;
  deal_value_rub: number;
  open_tasks: number;
  overdue: number;
  won_30d: number;
  lost_30d: number;
  conversion_30d: number;
}

export interface MyDealItem {
  amo_id: number;
  name: string;
  price_rub: number;
  stage_name: string;
  manager_name: string;
  silence_days: number;
  updated_at: string | null;
  created_at: string | null;
}

export interface MyTaskItem {
  amo_id: number;
  text: string;
  complete_till: string | null;
  overdue_days: number;
  lead_amo_id: number | null;
  lead_name: string | null;
}

export interface SearchLead {
  amo_id: number;
  name: string;
  price_rub: number;
  manager_name: string;
  updated_at: string | null;
}
export interface SearchTask {
  amo_id: number;
  text: string;
  complete_till: string | null;
  responsible_name: string;
  lead_amo_id: number | null;
}
export interface SearchContact {
  name: string;
  phone: string | null;
  email: string | null;
}
export interface SearchResult {
  q: string;
  leads: SearchLead[];
  tasks: SearchTask[];
  contacts: SearchContact[];
}

export interface VoiceParsed {
  command_id: number;
  intent: string;
  confidence: number;
  parsed: Record<string, unknown>;
  summary: string;
  clarification_needed: string | null;
  transcript?: string;
}
